import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type Theme = 'morning' | 'afternoon' | 'evening';
export type ThemeOverride = 'auto' | Theme;

const STORAGE_KEY = 'helloant.themeOverride';

function getAutoTheme(): Theme {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 18) return 'afternoon';
  return 'evening';
}

function readStoredOverride(): ThemeOverride {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'auto' || v === 'morning' || v === 'afternoon' || v === 'evening') {
      return v;
    }
  } catch {
    // localStorage may be unavailable (SSR, private mode, etc.)
  }
  return 'auto';
}

interface ThemeContextValue {
  theme: Theme;
  override: ThemeOverride;
  setOverride: (o: ThemeOverride) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [override, setOverrideState] = useState<ThemeOverride>(() => readStoredOverride());
  const [autoTheme, setAutoTheme] = useState<Theme>(() => getAutoTheme());

  // Tick auto-theme every minute.
  useEffect(() => {
    const id = setInterval(() => setAutoTheme(getAutoTheme()), 60_000);
    return () => clearInterval(id);
  }, []);

  const setOverride = useCallback((o: ThemeOverride) => {
    setOverrideState(o);
    try {
      localStorage.setItem(STORAGE_KEY, o);
    } catch {
      // ignore
    }
  }, []);

  const theme: Theme = override === 'auto' ? autoTheme : override;

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, override, setOverride }),
    [theme, override, setOverride],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const v = useContext(ThemeContext);
  if (!v) throw new Error('useTheme must be used inside ThemeProvider');
  return v;
}
